import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Send, Paperclip, Loader2, MessageCircle, Crown, GraduationCap, ShieldCheck,
  ChevronLeft, Search, Plus, X, FileVideo, Image as ImageIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { buildFileUrl, uploadFile } from '../lib/files';

const ROLE_BADGE = {
  ADMIN: { label: 'Admin', icon: Crown, cls: 'bg-[#C8102E] text-white border-[#C8102E]' },
  TRAINER: { label: 'Trainer', icon: GraduationCap, cls: 'bg-[#1B3A87] text-white border-[#1B3A87]' },
  FRONT_DESK: { label: 'Front Desk', icon: ShieldCheck, cls: 'bg-[#B08D57] text-white border-[#B08D57]' },
  CLIENT: { label: 'Client', icon: null, cls: 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700' },
};

const initials = (n = '') => n.split(' ').filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || 'U';
const formatTime = (iso) => {
  try {
    const d = new Date(iso); const today = new Date();
    if (d.toDateString() === today.toDateString())
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const RoleBadge = ({ role }) => {
  const meta = ROLE_BADGE[role] || ROLE_BADGE.CLIENT;
  const I = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-bold border ${meta.cls}`}>
      {I && <I className='w-2.5 h-2.5' />} {meta.label}
    </span>
  );
};

const NewMessageDialog = ({ open, onClose, onPicked }) => {
  const [directory, setDirectory] = useState([]);
  const [q, setQ] = useState('');
  useEffect(() => {
    if (!open) return;
    api.get('/dm/staff-directory').then((r) => setDirectory(r.data));
  }, [open]);
  if (!open) return null;
  const filtered = directory.filter((u) =>
    !q.trim() || `${u.full_name} ${u.role} ${u.title || ''}`.toLowerCase().includes(q.trim().toLowerCase())
  );
  return (
    <div className='fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3' data-testid='dm-new-dialog'>
      <Card className='w-full max-w-md bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700'>
        <div className='p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between'>
          <div className='font-display font-bold text-lg text-slate-900 dark:text-slate-50'>Start a new conversation</div>
          <button onClick={onClose} aria-label='Close' className='text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'><X className='w-4 h-4' /></button>
        </div>
        <div className='p-4 space-y-3'>
          <div className='relative'>
            <Search className='w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400' />
            <Input className='pl-9' value={q} onChange={(e) => setQ(e.target.value)} placeholder='Search staff, trainer, admin…' autoFocus />
          </div>
          <div className='max-h-[55vh] overflow-y-auto -mx-1 px-1 space-y-1.5'>
            {filtered.length === 0 ? (
              <div className='text-center py-8 text-sm text-slate-500'>No staff found.</div>
            ) : filtered.map((u) => (
              <button
                key={u.id}
                onClick={() => onPicked(u)}
                className='w-full flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-[#1B3A87] dark:hover:border-[#93C5FD] hover:shadow-sm text-left transition-all'
                data-testid={`dm-new-pick-${u.id}`}
              >
                <Avatar className='w-9 h-9 shrink-0'>
                  {u.avatar_url && <AvatarImage src={u.avatar_url} alt={u.full_name} />}
                  <AvatarFallback className='bg-[#1B3A87] text-white text-xs font-semibold'>{initials(u.full_name)}</AvatarFallback>
                </Avatar>
                <div className='flex-1 min-w-0'>
                  <div className='text-sm font-semibold text-slate-900 dark:text-slate-50 truncate'>{u.full_name}</div>
                  <div className='text-[11px] text-slate-500 dark:text-slate-400 truncate'>{u.title || ''}</div>
                </div>
                <RoleBadge role={u.role} />
              </button>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};

const AttachmentPreview = ({ file, onClear }) => {
  if (!file) return null;
  const isVid = file.type.startsWith('video/');
  return (
    <div className='flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-800 rounded-md text-xs border border-slate-200 dark:border-slate-700'>
      {isVid ? <FileVideo className='w-3.5 h-3.5 text-[#1B3A87] dark:text-[#93C5FD]' /> : <ImageIcon className='w-3.5 h-3.5 text-[#1B3A87] dark:text-[#93C5FD]' />}
      <span className='truncate max-w-[160px] text-slate-700 dark:text-slate-200'>{file.name}</span>
      <span className='text-slate-500'>· {(file.size / 1024 / 1024).toFixed(2)} MB</span>
      <button onClick={onClear} className='ml-1 text-slate-400 hover:text-[#B42318]' aria-label='Remove attachment'><X className='w-3 h-3' /></button>
    </div>
  );
};

const AttachmentView = ({ att }) => {
  if (!att) return null;
  const url = buildFileUrl(att.url || att.id);
  if (att.content_type?.startsWith('video/')) {
    return (
      <video src={url} controls preload='metadata' className='mt-2 rounded-md max-h-72 border border-slate-200 dark:border-slate-700 bg-black w-full max-w-md' data-testid={`dm-attachment-video-${att.id}`} />
    );
  }
  if (att.content_type?.startsWith('image/')) {
    return (
      <img src={url} alt={att.original_filename || 'attachment'} className='mt-2 rounded-md max-h-72 border border-slate-200 dark:border-slate-700' data-testid={`dm-attachment-image-${att.id}`} />
    );
  }
  return (
    <a href={url} target='_blank' rel='noopener noreferrer' className='mt-2 inline-flex items-center gap-2 text-xs text-[#1B3A87] dark:text-[#93C5FD] underline'>
      <Paperclip className='w-3.5 h-3.5' /> {att.original_filename || 'Attachment'}
    </a>
  );
};

export default function Messages() {
  const { user, loading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [activeOther, setActiveOther] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [showListOnMobile, setShowListOnMobile] = useState(true);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadThreads = useCallback(async () => {
    const { data } = await api.get('/dm/threads');
    setThreads(data);
    return data;
  }, []);

  const openThread = useCallback(async (otherUserId) => {
    if (!otherUserId) return;
    try {
      const { data: t } = await api.get(`/dm/threads/with/${otherUserId}`);
      setActiveThread(t);
      setActiveOther({
        id: t.other_user_id, full_name: t.other_user_name,
        role: t.other_user_role, avatar_url: t.other_user_avatar,
      });
      const { data: msgs } = await api.get(`/dm/threads/${t.id}/messages`);
      setMessages(msgs);
      setShowListOnMobile(false);
      // reload thread list to refresh unread counts
      loadThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cannot open thread');
    }
  }, [loadThreads]);

  // Initial load
  useEffect(() => {
    if (!user) return;
    loadThreads().then((rows) => {
      const targetUser = searchParams.get('with');
      if (targetUser) {
        openThread(targetUser);
      } else if (rows[0]) {
        // On desktop, auto-open the most recent thread
        if (window.innerWidth >= 1024) openThread(rows[0].other_user_id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Poll every 5s
  useEffect(() => {
    if (!user) return;
    const t = setInterval(() => {
      loadThreads();
      if (activeThread) {
        api.get(`/dm/threads/${activeThread.id}/messages`).then((r) => setMessages(r.data)).catch(() => {});
      }
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThread?.id, user?.id]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 75 * 1024 * 1024) { toast.error('File too large (max 75 MB)'); return; }
    setPendingFile(f);
  };

  const send = async (e) => {
    e?.preventDefault?.();
    if (!activeOther) return;
    const body = text.trim();
    if (!body && !pendingFile) return;
    setSending(true);
    try {
      let fileId = null;
      if (pendingFile) {
        setUploadingFile(true);
        try {
          const uploaded = await uploadFile(pendingFile, 'dm_attachment', api);
          fileId = uploaded.id;
        } finally { setUploadingFile(false); }
      }
      await api.post('/dm/send', { recipient_id: activeOther.id, body, attachment_file_id: fileId });
      setText('');
      setPendingFile(null);
      // refresh messages
      const { data } = await api.get(`/dm/threads/${activeThread.id}/messages`);
      setMessages(data);
      loadThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  if (loading) return <div className='p-10 text-slate-500'>Loading…</div>;
  if (!user) { navigate('/login'); return null; }

  return (
    <div className='p-3 sm:p-4 lg:p-6 max-w-7xl mx-auto'>
      <div className='flex items-center justify-between mb-3 sm:mb-4 flex-wrap gap-2'>
        <div>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Direct Messages</div>
          <h1 className='font-display text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-50 mt-1 flex items-center gap-2'>
            <MessageCircle className='w-6 h-6 text-[#1B3A87] dark:text-[#93C5FD]' /> Messages
          </h1>
        </div>
        <Button onClick={() => setNewOpen(true)} className='bg-[#1B3A87] hover:bg-[#16306F] text-white' data-testid='dm-new-button'>
          <Plus className='w-4 h-4 mr-1.5' /> New message
        </Button>
      </div>

      <Card className='border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden grid lg:grid-cols-[280px_1fr]' data-testid='dm-shell'>
        {/* Thread list */}
        <div className={`${showListOnMobile ? 'block' : 'hidden'} lg:block border-r border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950`}>
          <div className='p-3 border-b border-slate-200 dark:border-slate-800 text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 dark:text-slate-400'>
            Conversations ({threads.length})
          </div>
          <div className='max-h-[calc(100vh-260px)] overflow-y-auto' data-testid='dm-thread-list'>
            {threads.length === 0 ? (
              <div className='p-6 text-center text-sm text-slate-500 dark:text-slate-400'>
                No conversations yet.
                <div className='mt-2'><Button size='sm' variant='outline' onClick={() => setNewOpen(true)}>Start one</Button></div>
              </div>
            ) : threads.map((t) => {
              const active = activeThread?.id === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => openThread(t.other_user_id)}
                  className={`w-full p-3 flex items-start gap-3 text-left transition-colors ${
                    active
                      ? 'bg-white dark:bg-slate-900 border-l-2 border-l-[#1B3A87]'
                      : 'hover:bg-white dark:hover:bg-slate-900 border-l-2 border-l-transparent'
                  }`}
                  data-testid={`dm-thread-${t.id}`}
                >
                  <Avatar className='w-10 h-10 shrink-0'>
                    {t.other_user_avatar && <AvatarImage src={t.other_user_avatar} alt={t.other_user_name} />}
                    <AvatarFallback className='bg-[#1B3A87] text-white text-xs font-semibold'>{initials(t.other_user_name)}</AvatarFallback>
                  </Avatar>
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-2'>
                      <span className='text-sm font-semibold text-slate-900 dark:text-slate-50 truncate'>{t.other_user_name}</span>
                      <RoleBadge role={t.other_user_role} />
                    </div>
                    <div className='text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5'>{t.last_message_preview || 'No messages yet'}</div>
                  </div>
                  <div className='flex flex-col items-end gap-1 shrink-0'>
                    <span className='text-[10px] text-slate-400'>{t.last_message_at ? formatTime(t.last_message_at) : ''}</span>
                    {t.unread_count > 0 && (
                      <span className='inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#C8102E] text-white text-[10px] font-bold' data-testid={`dm-unread-${t.id}`}>
                        {t.unread_count}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Conversation */}
        <div className={`${showListOnMobile ? 'hidden' : 'flex'} lg:flex flex-col h-[calc(100vh-220px)] min-h-[480px]`}>
          {!activeThread || !activeOther ? (
            <div className='flex-1 flex items-center justify-center text-sm text-slate-500 dark:text-slate-400 p-6 text-center'>
              <div>
                <MessageCircle className='w-10 h-10 mx-auto text-slate-300 dark:text-slate-600 mb-2' />
                <div>Select a conversation or start a new one.</div>
              </div>
            </div>
          ) : (
            <>
              <div className='p-3 border-b border-slate-200 dark:border-slate-800 flex items-center gap-3 bg-white dark:bg-slate-900'>
                <button onClick={() => setShowListOnMobile(true)} className='lg:hidden text-slate-500 dark:text-slate-300' aria-label='Back'>
                  <ChevronLeft className='w-5 h-5' />
                </button>
                <Avatar className='w-9 h-9'>
                  {activeOther.avatar_url && <AvatarImage src={activeOther.avatar_url} alt={activeOther.full_name} />}
                  <AvatarFallback className='bg-[#1B3A87] text-white text-xs font-semibold'>{initials(activeOther.full_name)}</AvatarFallback>
                </Avatar>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2'>
                    <span className='font-semibold text-sm text-slate-900 dark:text-slate-50 truncate'>{activeOther.full_name}</span>
                    <RoleBadge role={activeOther.role} />
                  </div>
                </div>
              </div>

              <div
                ref={scrollRef}
                className='flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 bg-slate-50 dark:bg-slate-950'
                data-testid='dm-messages'
              >
                {messages.length === 0 ? (
                  <div className='text-center py-10 text-sm text-slate-500 dark:text-slate-400'>
                    Send the first message to start the conversation.
                  </div>
                ) : messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div
                      key={m.id}
                      className={`flex items-end gap-2 ${mine ? 'justify-end' : 'justify-start'}`}
                      data-testid={`dm-msg-${m.id}`}
                    >
                      {!mine && (
                        <Avatar className='w-7 h-7 shrink-0'>
                          {m.sender_avatar && <AvatarImage src={m.sender_avatar} alt={m.sender_name} />}
                          <AvatarFallback className='bg-[#1B3A87] text-white text-[10px] font-semibold'>{initials(m.sender_name)}</AvatarFallback>
                        </Avatar>
                      )}
                      <div className={`max-w-[78%] sm:max-w-[68%] ${mine ? 'items-end' : 'items-start'} flex flex-col`}>
                        <div className={`px-3.5 py-2 rounded-2xl ${
                          mine
                            ? 'bg-[#1B3A87] text-white rounded-br-sm'
                            : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-bl-sm'
                        } text-sm whitespace-pre-wrap break-words leading-relaxed`}>
                          {m.body || (m.attachment ? <span className='italic opacity-80'>(attachment)</span> : '')}
                        </div>
                        {m.attachment && <AttachmentView att={m.attachment} />}
                        <div className={`mt-1 text-[10px] text-slate-400 dark:text-slate-500 ${mine ? 'text-right' : 'text-left'}`}>
                          {formatTime(m.created_at)}
                          {mine && m.read_at && <span className='ml-1'>· Read</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={send} className='border-t border-slate-200 dark:border-slate-800 p-3 bg-white dark:bg-slate-900 space-y-2'>
                {pendingFile && <AttachmentPreview file={pendingFile} onClear={() => setPendingFile(null)} />}
                <div className='flex items-center gap-2'>
                  <input
                    ref={fileInputRef}
                    type='file'
                    onChange={onPickFile}
                    accept='image/*,video/mp4,video/webm,video/quicktime'
                    className='hidden'
                    data-testid='dm-file-input'
                  />
                  <button
                    type='button'
                    onClick={() => fileInputRef.current?.click()}
                    className='h-10 w-10 inline-flex items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                    title='Attach video or image'
                    data-testid='dm-attach-button'
                  >
                    <Paperclip className='w-4 h-4' />
                  </button>
                  <Input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={`Message ${activeOther.full_name}…`}
                    className='h-10 bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100'
                    data-testid='dm-input'
                    maxLength={4000}
                  />
                  <Button
                    type='submit'
                    disabled={(!text.trim() && !pendingFile) || sending}
                    className='bg-[#1B3A87] hover:bg-[#16306F] text-white h-10 shrink-0'
                    data-testid='dm-send-button'
                  >
                    {sending || uploadingFile ? <Loader2 className='w-4 h-4 animate-spin' /> : <Send className='w-4 h-4' />}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </Card>

      <NewMessageDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onPicked={(u) => {
          setNewOpen(false);
          setSearchParams({ with: u.id });
          openThread(u.id);
        }}
      />
    </div>
  );
}
